/**
 * THE READER'S OWN CORNER — the control that opens what the map has noticed about them.
 *
 * Theirs puts a round YOU beside the count of who else is exploring, top right, and one press opens
 * a card: an eyebrow, the posture it has inferred, the sentence that earned it, and the atlas —
 * a row per scripture with how far through it the reader has got.
 *
 * Ours had every part of that and no door to it. The card lived inside the wordmark's menu, behind
 * a fold, so reaching the one block of the app that says anything about the person reading took two
 * presses and a scroll past the licences. The menu keeps what only we have — the legend, sources,
 * halls, disagreements, language, offline — and the reader's own state moves here, where theirs is.
 *
 * It is a button and a card, not a `<details>`: the card is positioned over the map and a details'
 * open/shut is the wrong shape for something that has to close on Escape and on a press outside.
 */
import { useEffect, useRef } from 'react';
import { useLang } from '../i18n/lang';

export interface MapYouProps {
  /** Whether the card is showing. Held by the caller, like every other overlay on this map. */
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** The card itself, so this file owns the door and not what is behind it. */
  children: React.ReactNode;
  /**
   * How many names the reader has walked, for the dot on the button.
   *
   * Theirs shows a plain YOU. Ours shows the same until there is something to say, and a reader who
   * has walked nowhere is told so inside rather than by a badge saying nought.
   */
  walked: number;
}

export function MapYou({ open, onToggle, onClose, children, walked }: MapYouProps): React.JSX.Element {
  const { t } = useLang();
  const box = useRef<HTMLDivElement>(null);

  /*
   * ESCAPE AND A PRESS OUTSIDE, both, because either one alone strands somebody. The map's other
   * overlays use a scrim for this; a corner card is small enough that a scrim over the whole map
   * would darken more than it opens.
   */
  useEffect(() => {
    if (!open) return undefined;
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    const away = (event: MouseEvent): void => {
      const target = event.target;
      if (target instanceof Node && box.current !== null && !box.current.contains(target)) onClose();
    };
    document.addEventListener('keydown', key);
    /* Capture, so a press that a layer below stops still closes this. */
    document.addEventListener('mousedown', away, true);
    return () => {
      document.removeEventListener('keydown', key);
      document.removeEventListener('mousedown', away, true);
    };
  }, [open, onClose]);

  return (
    <div className="map-you" ref={box} data-map-chrome>
      <button
        type="button"
        className="map-you-toggle"
        aria-expanded={open}
        aria-controls="map-you-card"
        aria-label={t('you.open')}
        onClick={onToggle}
      >
        <span aria-hidden="true">{t('you.short')}</span>
        {/* A mark rather than a number: the count is in the card, and a badge of nought says nothing
            worth the room. */}
        {walked > 0 && <span className="map-you-walked" aria-hidden="true" />}
      </button>
      {open && (
        <div id="map-you-card" className="map-you-card">
          {children}
        </div>
      )}
    </div>
  );
}
